/**
 * SettingsLayout - Phase 2: Einheitliche Einstellungs-Hülle
 * Linke Navigation (gruppiert) + Live-Suche + Inhaltsbereich mit eigener Route pro Einstellung.
 * Bestehende "advanced"-Panels werden eingebettet (<SettingsPage embedded forcedCard=... />),
 * große Verwaltungsseiten (Services, Produkte, Studios ...) werden verlinkt (öffnen separat).
 */
import React, { useMemo, useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { Search, ChevronRight, ChevronDown, ExternalLink, Settings as SettingsIcon } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { settingsCategories } from '@/pages/MainSettingsPage';
import SettingsPage from '@/pages/SettingsPage';
// Kleine Standalone-Einstellungsseiten, die jetzt rechts eingebettet werden (linke Navi bleibt stehen)
import EPostSettings from '@/pages/EPostSettings';
import ErpSettingsPage from '@/pages/ErpSettingsPage';
import SumUpSettingsPage from '@/pages/SumUpSettingsPage';
import PaymentMethodsSettingsPage from '@/pages/PaymentMethodsSettingsPage';
import CardExpirySettingsPage from '@/pages/CardExpirySettingsPage';
import EmailDiagnosticsPage from '@/pages/EmailDiagnosticsPage';
import HolidaysSettingsPage from '@/pages/HolidaysSettingsPage';
// Große Funktionsseiten – ebenfalls rechts eingebettet
import StudiosPage from '@/pages/StudiosPage';
import ServicesPage from '@/pages/ServicesPage';
import PersonalPage from '@/pages/PersonalPage';
import ProductsPage from '@/pages/ProductsPage';
import ResourcesPage from '@/pages/ResourcesPage';
import AutomationRulesPage from '@/pages/AutomationRulesPage';
import ReEngagementSettingsPage from '@/pages/ReEngagementSettingsPage';
import PushConfigsPage from '@/pages/PushConfigsPage';
import EmailTemplatesPage from '@/pages/EmailTemplatesPage';
import WhatsAppTemplatesPage from '@/pages/WhatsAppTemplatesPage';
import EmailLogsPage from '@/pages/EmailLogsPage';
import PortalSettingsPage from '@/pages/PortalSettingsPage';
import MobileAppPage from '@/pages/MobileAppPage';
import StudioBonusSettingsPage from '@/pages/StudioBonusSettingsPage';
import GoogleBusinessHoursPage from '@/pages/GoogleBusinessHoursPage';
import MindbodyIntegrationPage from '@/pages/MindbodyIntegrationPage';
import DensityLearningPage from '@/pages/DensityLearningPage';
import ReferralSourcesSettingsPage from '@/pages/ReferralSourcesSettingsPage';
import PhysioBillingSettingsPage from '@/pages/PhysioBillingSettingsPage';
import CalendarMenuSettingsPage from '@/pages/CalendarMenuSettingsPage';
import CustomFieldsSettingsPage from '@/pages/CustomFieldsSettingsPage';
import OnboardingBuilderSettingsPage from '@/pages/OnboardingBuilderSettingsPage';
import ModulesSettingsPage from '@/pages/ModulesSettingsPage';
import MenuLayoutSettingsPage from '@/pages/MenuLayoutSettingsPage';
import BundleRemindersPage from '@/pages/BundleRemindersPage';
import CalendarColumnsSettingsPage from '@/pages/CalendarColumnsSettingsPage';

// Registry: Karten-ID -> einbettbare Seiten-Komponente (im rechten Bereich gerendert)
export const EMBEDDABLE_PAGES = {
  epost: EPostSettings,
  erp: ErpSettingsPage,
  'bundle-reminders': BundleRemindersPage,
  sumup: SumUpSettingsPage,
  'payment-methods': PaymentMethodsSettingsPage,
  card_expiry: CardExpirySettingsPage,
  'email-diagnostics': EmailDiagnosticsPage,
  holidays: HolidaysSettingsPage,
  studios: StudiosPage,
  services: ServicesPage,
  trainers: PersonalPage,
  products: ProductsPage,
  resources: ResourcesPage,
  automation: AutomationRulesPage,
  reengagement: ReEngagementSettingsPage,
  push_configs: PushConfigsPage,
  email_templates: EmailTemplatesPage,
  whatsapp_templates: WhatsAppTemplatesPage,
  logs: EmailLogsPage,
  portal: PortalSettingsPage,
  'mobile-app': MobileAppPage,
  'studio-bonus': StudioBonusSettingsPage,
  'google-business-hours': GoogleBusinessHoursPage,
  'mindbody-integration': MindbodyIntegrationPage,
  'density-learning': DensityLearningPage,
  'referral-sources': ReferralSourcesSettingsPage,
  'physio-billing': PhysioBillingSettingsPage,
  'calendar-menu': CalendarMenuSettingsPage,
  'calendar-columns': CalendarColumnsSettingsPage,
  'custom-fields': CustomFieldsSettingsPage,
  'onboarding-builder': OnboardingBuilderSettingsPage,
  'modules': ModulesSettingsPage,
  'menu-layout': MenuLayoutSettingsPage,
  timetracking: { component: PersonalPage, props: { defaultTab: 'stunden' } },
  payroll: { component: PersonalPage, props: { defaultTab: 'lohn' } },
};

const API = process.env.REACT_APP_BACKEND_URL;

const TONE_CLASSES = {
  green: 'bg-green-100 text-green-700',
  amber: 'bg-amber-100 text-amber-700',
  gray: 'bg-gray-100 text-gray-500',
};

const StatusBadge = ({ badge }) => {
  if (!badge || !badge.label) return null;
  const cls = TONE_CLASSES[badge.tone] || TONE_CLASSES.gray;
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium ${cls}`}
      data-testid={`settings-badge-${badge.status}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${badge.tone === 'green' ? 'bg-green-500' : badge.tone === 'amber' ? 'bg-amber-500' : 'bg-gray-400'}`} />
      {badge.label}
    </span>
  );
};

// Erkennt, ob eine Karte ein einbettbares advanced-Panel ist, und extrahiert dessen Key.
const toItem = (card, section) => {
  const path = card.path || '';
  const m = path.match(/\/settings\/advanced\?(?:tab|card)=([\w-]+)/);
  const entry = EMBEDDABLE_PAGES[card.id] || null;
  const PageComponent = entry ? (entry.component || entry) : null;
  const pageProps = entry && entry.props ? entry.props : null;
  return {
    ...card,
    section,
    embedded: !!m || !!PageComponent,
    panelKey: m ? m[1] : null,
    PageComponent,
    pageProps,
  };
};

const SettingsLayout = () => {
  const { panelId } = useParams();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [statuses, setStatuses] = useState({});

  useEffect(() => {
    const token = localStorage.getItem('token');
    axios
      .get(`${API}/api/settings/status`, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then(res => setStatuses(res.data?.statuses || {}))
      .catch(() => setStatuses({}));
  }, []);

  const items = useMemo(() => {
    const flat = [];
    settingsCategories.forEach(cat => cat.cards.forEach(card => flat.push(toItem(card, cat.title))));
    return flat;
  }, []);

  const activeItem = useMemo(
    () => (panelId ? items.find(i => i.id === panelId) : null),
    [panelId, items]
  );

  // Akkordeon: nur EIN Bereich gleichzeitig offen
  const [openSection, setOpenSection] = useState(null);
  useEffect(() => {
    setOpenSection(activeItem ? activeItem.section : (settingsCategories[0]?.title || null));
  }, [activeItem]);

  const q = query.trim().toLowerCase();
  const filteredCategories = useMemo(() => {
    return settingsCategories
      .map(cat => ({
        title: cat.title,
        cards: cat.cards
          .map(c => toItem(c, cat.title))
          .filter(c =>
            !q ||
            (c.title || '').toLowerCase().includes(q) ||
            (c.description || '').toLowerCase().includes(q)
          ),
      }))
      .filter(cat => cat.cards.length > 0);
  }, [q]);

  const openItem = (item) => {
    if (item.embedded) navigate(`/settings/${item.id}`);
    else navigate(item.path);
  };

  // Phase 3: Alte "KI & API"-Route weich in den neuen KI-Bereich umleiten
  if (panelId === 'ki-api') return <Navigate to="/settings/ki" replace />;
  // Phase 3: Farben ist in "Öffnungszeiten & Kalender" integriert
  if (panelId === 'colors') return <Navigate to="/settings/availability" replace />;
  // Unbekannte / externe Panel-ID -> auf passende Seite oder Übersicht leiten
  if (panelId && !activeItem) return <Navigate to="/settings" replace />;
  if (panelId && activeItem && !activeItem.embedded) {
    return <Navigate to={activeItem.path} replace />;
  }

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto" data-testid="settings-layout">
      <div className="mb-6 flex items-center gap-2">
        <SettingsIcon className="h-6 w-6 text-gray-600" />
        <h1 className="text-2xl font-bold text-gray-900">Einstellungen</h1>
      </div>

      <div className="flex flex-col md:flex-row gap-6">
        {/* Linke Navigation */}
        <aside className="w-full md:w-72 flex-shrink-0" data-testid="settings-nav">
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Einstellung suchen …"
              className="pl-9"
              data-testid="settings-search-input"
            />
          </div>

          <nav className="space-y-2 pr-1" data-testid="settings-nav-accordion">
            {filteredCategories.length === 0 && (
              <p className="text-sm text-gray-400 px-2" data-testid="settings-search-empty">
                Keine Einstellung gefunden für „{query}".
              </p>
            )}
            {filteredCategories.map(cat => {
              const isOpen = !!q || openSection === cat.title;
              return (
                <div key={cat.title} className="border border-gray-100 rounded-lg overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setOpenSection(s => (s === cat.title ? null : cat.title))}
                    className="w-full flex items-center justify-between px-3 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors"
                    data-testid={`settings-section-toggle-${cat.title}`}
                  >
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                      {cat.title}
                    </span>
                    <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {isOpen && (
                    <ul className="p-1 space-y-0.5">
                      {cat.cards.map(item => {
                        const Icon = item.icon;
                        const isActive = activeItem && activeItem.id === item.id;
                        return (
                          <li key={item.id}>
                            <button
                              type="button"
                              onClick={() => openItem(item)}
                              className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md text-sm text-left transition-colors ${
                                isActive
                                  ? 'bg-teal-50 text-teal-700 font-medium'
                                  : 'text-gray-700 hover:bg-gray-100'
                              }`}
                              data-testid={`settings-nav-${item.id}`}
                            >
                              <span className={`p-1.5 rounded-md ${item.bgColor || 'bg-gray-100'}`}>
                                <Icon className={`h-4 w-4 ${item.iconColor || 'text-gray-600'}`} />
                              </span>
                              <span className="flex-1 truncate">{item.title}</span>
                              <StatusBadge badge={statuses[item.id]} />
                              {item.embedded ? (
                                <ChevronRight className="h-4 w-4 text-gray-300 flex-shrink-0" />
                              ) : (
                                <ExternalLink className="h-3.5 w-3.5 text-gray-300 flex-shrink-0" />
                              )}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              );
            })}
          </nav>
        </aside>

        {/* Inhaltsbereich */}
        <main className="flex-1 min-w-0" data-testid="settings-content">
          {activeItem && activeItem.embedded ? (
            <div className="rounded-lg border bg-white p-4 md:p-6">
              <div className="mb-4 flex items-center gap-3 border-b pb-3">
                <span className={`p-2 rounded-lg ${activeItem.bgColor || 'bg-gray-100'}`}>
                  {React.createElement(activeItem.icon, { className: `h-5 w-5 ${activeItem.iconColor || 'text-gray-600'}` })}
                </span>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">{activeItem.title}</h2>
                  {activeItem.description && (
                    <p className="text-xs text-gray-500">{activeItem.description}</p>
                  )}
                </div>
              </div>
              {activeItem.PageComponent
                ? <div className="settings-embedded-body">{React.createElement(activeItem.PageComponent, { embedded: true, ...(activeItem.pageProps || {}) })}</div>
                : <SettingsPage embedded forcedCard={activeItem.panelKey} key={activeItem.panelKey} />}
            </div>
          ) : (
            <div data-testid="settings-overview">
              <div className="rounded-lg border bg-gradient-to-br from-teal-50 to-white p-6">
                <h2 className="text-lg font-semibold text-gray-800 mb-1">Willkommen in den Einstellungen</h2>
                <p className="text-sm text-gray-600">
                  Wähle links einen Bereich aus oder nutze die Suche. Einträge mit
                  <ChevronRight className="inline h-4 w-4 mx-0.5 text-gray-400" /> öffnen hier,
                  Einträge mit <ExternalLink className="inline h-3.5 w-3.5 mx-0.5 text-gray-400" /> öffnen eine eigene Seite.
                </p>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default SettingsLayout;
